import { useState, useEffect, useCallback, useRef } from "react";
import { z } from "zod";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { supabase as defaultSupabase } from "@/integrations/supabase/client";
import { Document } from "@/types/document";
import { WorkflowState, MultiSheetWorkflowState, isMultiSheetState } from "@/types/workflow";
import { parseWorkflowState, parseMultiSheetState } from "@/lib/workflowSchema";
import { splitForStorage, mergeFromStorage } from "@/lib/workflowDetailsSplit";
import {
  compactWorkflowState,
  expandCompactWorkflow,
  isCompactFormat,
} from "@/lib/compactWorkflow";
import {
  fetchAllDetailsForWorkflow,
  pushDetailsForWorkflow,
  pushBucketAttachmentsForWorkflow,
} from "@/lib/entityDetailsBulk";
import { logger } from "@/lib/logger";
import { clearOverlayScope } from "@/lib/threeD/blockOverlayStorage";


// --- Input validation schemas ---

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const titleSchema = z.string().trim().min(1, "Title is required").max(500, "Title too long");
// Held in sync with the edge-fn `validCategories` allowlist
// (`supabase/functions/save-workflow/index.ts`). `enterprise-tenant` is the
// admin-managed tenant-config doc-type — without it, the SaveDialog update
// path rejects tenant docs before reaching the server.
const categorySchema = z.enum(["workflow", "export-png", "enterprise-tenant"], { errorMap: () => ({ message: "Invalid category" }) });
const documentIdSchema = z.string().regex(UUID_REGEX, "Invalid document ID format");

const MAX_PAYLOAD_BYTES = 1_048_576;

// Unpack the `_multiSheet` envelope and auto-expand compact-encoded
// payloads (rows written after the `_compact: true` flag landed).
// Older rows without the flag fall through unchanged so existing data
// keeps loading.
function unpackMultiSheetEnvelope(
  packed: { _multiSheet?: boolean; _compact?: boolean; data?: unknown },
): {
  name?: string;
  sheets: unknown[];
  crossSheetConnectors: unknown[];
  activeSheetIndex?: number;
} {
  const data = (packed.data ?? {}) as Record<string, unknown>;
  if (packed._compact === true || isCompactFormat(data)) {
    return expandCompactWorkflow(data) as ReturnType<
      typeof unpackMultiSheetEnvelope
    >;
  }
  return data as ReturnType<typeof unpackMultiSheetEnvelope>;
}

interface SupabaseCredentialsMessage {
  type: "supabase-credentials";
  supabaseUrl: string;
  supabaseKey: string;
  accessToken?: string;
}

function extractCredentials(data: unknown): SupabaseCredentialsMessage | null {
  if (!data || typeof data !== "object") return null;
  const msg = data as Record<string, unknown>;
  if (msg.type !== "supabase-credentials") return null;

  let source = msg as Record<string, unknown>;
  if (msg.payload && typeof msg.payload === "object") {
    source = msg.payload as Record<string, unknown>;
  }

  const url = source.supabaseUrl;
  const key = source.supabaseKey ?? source.supabaseAnonKey;
  const token = source.accessToken;

  if (typeof url !== "string" || typeof key !== "string") return null;
  if (!url.startsWith("https://") || key.length < 10) return null;

  return {
    type: "supabase-credentials",
    supabaseUrl: url,
    supabaseKey: key,
    accessToken: typeof token === "string" ? token : undefined,
  };
}

// --- Allowed origins for postMessage ---
const ALLOWED_ORIGINS: string[] = ["https://lovable.dev", "https://lovable.app", "https://engicoro.se"];
const LOVABLE_SUBDOMAIN_REGEX =
  /^https:\/\/[a-z0-9][a-z0-9-]*[a-z0-9]\.(lovable\.app|lovable\.dev|lovableproject\.com)$/;

function isAllowedOrigin(origin: string): boolean {
  if (ALLOWED_ORIGINS.includes(origin)) return true;
  return LOVABLE_SUBDOMAIN_REGEX.test(origin);
}

function isEmbeddedInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export interface ConnectionDebugInfo {
  isIframe: boolean;
  credentialsReceived: boolean;
  hasAccessToken: boolean;
  lastMessageOrigin: string | null;
  lastMessageType: string | null;
  rejectedOrigins: string[];
  error: string | null;
}

export function useDocuments() {
  const isIframe = isEmbeddedInIframe();
  const [connected, setConnected] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const clientRef = useRef<SupabaseClient>(defaultSupabase);
  const [debugInfo, setDebugInfo] = useState<ConnectionDebugInfo>({
    isIframe,
    credentialsReceived: false,
    hasAccessToken: false,
    lastMessageOrigin: null,
    lastMessageType: null,
    rejectedOrigins: [],
    error: null,
  });

  // Primary path: use the project's own Supabase client whenever the
  // user is authenticated against it. Works both standalone and inside
  // the Lovable iframe — no postMessage handshake required.
  useEffect(() => {
    let cancelled = false;

    const adoptDefaultClient = (token: string | null) => {
      if (cancelled) return;
      clientRef.current = defaultSupabase;
      setAuthToken(token);
      setConnected(true);
      setDebugInfo((prev) => ({
        ...prev,
        credentialsReceived: true,
        hasAccessToken: !!token,
        lastMessageType: 'default-client',
        error: null,
      }));
    };

    void defaultSupabase.auth.getSession().then(({ data }) => {
      if (data.session?.access_token) {
        adoptDefaultClient(data.session.access_token);
      }
    });

    const { data: sub } = defaultSupabase.auth.onAuthStateChange((_evt, session) => {
      if (session?.access_token) {
        adoptDefaultClient(session.access_token);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Secondary (legacy) path: accept postMessage credentials from a host
  // app (white-label embeds) so existing integrations keep working.
  useEffect(() => {
    if (!isIframe) return;

    const handleMessage = (event: MessageEvent) => {
      const msgType = event.data?.type;

      if (!isAllowedOrigin(event.origin)) {
        if (msgType === "supabase-credentials") {
          setDebugInfo((prev) => ({
            ...prev,
            lastMessageOrigin: event.origin,
            lastMessageType: String(msgType),
            rejectedOrigins: [...prev.rejectedOrigins.slice(-4), event.origin],
            error: `Origin "${event.origin}" not in allowlist`,
          }));
        }
        return;
      }

      const data = event.data;
      const creds = extractCredentials(data);

      if (msgType === "supabase-credentials" && !creds) {
        setDebugInfo((prev) => ({
          ...prev,
          lastMessageOrigin: event.origin,
          lastMessageType: String(msgType),
          error:
            'Received supabase-credentials but could not extract valid fields.',
        }));
        return;
      }

      if (!creds) return;

      logger.info("Received Supabase credentials from parent");

      setDebugInfo((prev) => ({
        ...prev,
        credentialsReceived: true,
        hasAccessToken: !!creds.accessToken,
        lastMessageOrigin: event.origin,
        lastMessageType: "supabase-credentials",
        error: null,
      }));

      const headers: Record<string, string> = {};
      if (creds.accessToken) {
        headers["Authorization"] = `Bearer ${creds.accessToken}`;
      }

      const client = createClient(creds.supabaseUrl, creds.supabaseKey, {
        global: { headers },
      });

      clientRef.current = client;
      setConnected(true);

      if (creds.accessToken) {
        setAuthToken(creds.accessToken);
      }
    };

    window.addEventListener("message", handleMessage);

    try {
      let targetOrigin = "*";
      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (isAllowedOrigin(referrerOrigin)) {
          targetOrigin = referrerOrigin;
        }
      } catch {
        // referrer may be empty or invalid
      }
      window.parent.postMessage({ type: "request-supabase-credentials" }, targetOrigin);
    } catch {
      // Cross-origin — parent may not respond
    }

    return () => window.removeEventListener("message", handleMessage);
  }, [isIframe]);

  const getClient = useCallback(() => clientRef.current, []);

  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    try {
      const client = getClient();

      // A-027: getClaims är typed i @supabase/supabase-js sedan 2.95.x;
      // tidigare unsafe-cast på client.auth dolde faktisk type-säkerhet.
      if (authToken) {
        const { data, error } = await client.auth.getClaims(authToken);
        if (!error && data?.claims?.sub) return data.claims.sub;
      }

      const {
        data: { user },
        error,
      } = await client.auth.getUser();
      if (error || !user) return null;
      return user.id;
    } catch {
      return null;
    }
  }, [getClient, authToken]);

  const saveDocument = useCallback(
    async (
      title: string,
      workflowState: WorkflowState | MultiSheetWorkflowState,
      category: string = "workflow",
      existingId?: string,
      isOfficial: boolean = false,
    ): Promise<{ id: string } | null> => {
      if (!connected) {
        throw new Error("Database not available in standalone mode");
      }

      const titleResult = titleSchema.safeParse(title);
      if (!titleResult.success) {
        throw new Error(`Invalid title: ${titleResult.error.issues[0]?.message}`);
      }

      const categoryResult = categorySchema.safeParse(category);
      if (!categoryResult.success) {
        throw new Error(`Invalid category: ${categoryResult.error.issues[0]?.message}`);
      }

      if (existingId) {
        const idResult = documentIdSchema.safeParse(existingId);
        if (!idResult.success) {
          throw new Error(`Invalid document ID: ${idResult.error.issues[0]?.message}`);
        }
      }

      // Lager C: split state — workflows.content får lean struktur,
      // descriptions/icons/subjects/labels/waypoints flyttas till
      // entity_details. detailsToWrite samlas och bulk-pushas efter
      // lyckad save (workflow_id krävs för att RLS:en ska kunna
      // verifiera ägarskap).
      const {
        lean: leanState,
        details: detailsToWrite,
        attachments: attachmentsToUpload,
      } = splitForStorage(workflowState);

      // Handle both single-sheet and multi-sheet formats
      let body: Record<string, unknown>;

      if (isMultiSheetState(leanState)) {
        const validated = parseMultiSheetState(leanState);
        if (!validated) {
          throw new Error("Invalid multi-sheet workflow state structure");
        }
        // Compact the validated lean state — encodes every block/
        // connector/group key as its short alias (n, d, dd, c, i, …).
        // Round-trip through `expandCompactWorkflow` on read; older
        // rows that lack `_compact` fall through unchanged.
        const compactedMulti = compactWorkflowState(validated) as Record<
          string,
          unknown
        >;
        body = {
          title: titleResult.data,
          blocks: { _multiSheet: true, _compact: true, data: compactedMulti },
          connectors: [],
          groups: [],
          workflow_name: validated.name || "",
          coordinate_origin: validated.coordinateOrigin || "center",
          category: categoryResult.data,
          is_official: isOfficial,
        };
      } else {
        // splitForStorage returnerar alltid multi-sheet, så denna gren
        // körs i praktiken inte längre — håller kvar för defensiv
        // bakåtkompat ifall split-kontraktet ändras.
        const validatedState = parseWorkflowState(leanState);
        if (!validatedState) {
          throw new Error("Invalid workflow state structure");
        }
        body = {
          title: titleResult.data,
          blocks: validatedState.blocks,
          connectors: validatedState.connectors,
          groups: validatedState.groups,
          workflow_name: validatedState.name || "",
          coordinate_origin: validatedState.coordinateOrigin || "center",
          category: categoryResult.data,
          is_official: isOfficial,
        };
      }
      if (existingId) {
        body.document_id = existingId;
      }

      const payload = JSON.stringify(body);
      const payloadSize = new TextEncoder().encode(payload).length;
      if (payloadSize > MAX_PAYLOAD_BYTES) {
        throw new Error(
          `Payload too large (${Math.round(payloadSize / 1024)}KB). Maximum is ${MAX_PAYLOAD_BYTES / 1024}KB.`,
        );
      }

      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error("Authentication required");
      }

      const client = getClient();
      const { data, error } = await client.functions.invoke("save-workflow", {
        body,
      });

      if (error) {
        throw new Error(`Save failed: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || "Save failed");
      }

      // Lager C: efter lyckad save → push entity_details. Fire-and-forget
      // (logas men avbryter inte). Ny workflow_id används om existingId
      // saknades. Tom details-array → no-op.
      if (detailsToWrite.length > 0 && data.document_id) {
        try {
          await pushDetailsForWorkflow(data.document_id, detailsToWrite);
        } catch (err) {
          logger.warn('Lager C: pushDetailsForWorkflow failed', {
            workflowId: data.document_id,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      // Long-form details (`description_md` > 1 MB) — upload to the
      // entity-attachments bucket AFTER pushDetailsForWorkflow so the
      // parent entity_details row exists for the FK lookup inside
      // register_entity_attachment. Fail-soft like the details push.
      if (attachmentsToUpload.length > 0 && data.document_id) {
        try {
          const r = await pushBucketAttachmentsForWorkflow(
            data.document_id,
            attachmentsToUpload,
          );
          if (r.failed > 0) {
            logger.warn('Lager C: some bucket attachments failed', {
              workflowId: data.document_id,
              uploaded: r.uploaded,
              failed: r.failed,
            });
          }
        } catch (err) {
          logger.warn('Lager C: pushBucketAttachmentsForWorkflow failed', {
            workflowId: data.document_id,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      return { id: data.document_id };
    },
    [connected, getCurrentUserId, getClient],
  );

  const listDocuments = useCallback(async (): Promise<Document[]> => {
    if (!connected) return [];

    const userId = await getCurrentUserId();
    if (!userId) {
      logger.warn("No authenticated user — cannot list documents");
      return [];
    }

    const client = getClient();
    const { data, error } = await client
      .from("workflows")
      .select("id, title, category, is_pinned, is_official, pinned_by, pinned_at, created_by, created_at, updated_at")
      .eq("created_by", userId)
      .order("is_pinned", { ascending: false })
      .order("updated_at", { ascending: false });

    if (error) throw error;
    // A-041: SELECT:en speglar nu Document-type:en exakt — `description`
    // togs bort från Document eftersom kolumnen aldrig fanns på
    // workflows-tabellen. Onödig cast borttagen.
    return data ?? [];
  }, [connected, getCurrentUserId, getClient]);

  const loadDocument = useCallback(
    async (id: string): Promise<WorkflowState | MultiSheetWorkflowState | null> => {
      if (!connected) return null;

      const idResult = documentIdSchema.safeParse(id);
      if (!idResult.success) {
        throw new Error("Invalid document ID format");
      }

      const userId = await getCurrentUserId();
      if (!userId) {
        logger.warn("No authenticated user — cannot load document");
        return null;
      }

      const client = getClient();
      const { data, error } = await client
        .from("workflows")
        .select("blocks, connectors, groups, workflow_name, coordinate_origin")
        .eq("id", id)
        .eq("created_by", userId)
        .single();

      if (error) throw error;
      if (!data) return null;

      let leanState: WorkflowState | MultiSheetWorkflowState | null;

      // Encrypted-at-rest workspaces: `blocks` holds an AES-GCM ciphertext
      // string keyed off the user's email. Decrypt before validation.
      if (data.coordinate_origin === 'encrypted') {
        const { data: sess } = await client.auth.getSession();
        const email = sess.session?.user?.email;
        if (!email) throw new Error('Authentication required to decrypt workspace');
        const { decryptWorkspace } = await import('@/lib/workspaceEncryption');
        const decrypted = await decryptWorkspace<WorkflowState | MultiSheetWorkflowState>(
          data.blocks as unknown as string,
          email,
        );
        const named = { ...decrypted, name: data.workflow_name || decrypted.name || '' };
        leanState = isMultiSheetState(named)
          ? (parseMultiSheetState(named) as MultiSheetWorkflowState | null)
          : (parseWorkflowState(named as WorkflowState) as WorkflowState | null);
      } else {
        // Check if this is a multi-sheet document (packed into blocks column)
        const blocksData = data.blocks as Record<string, unknown>;
        if (blocksData && typeof blocksData === 'object' && '_multiSheet' in blocksData) {
          const inner = unpackMultiSheetEnvelope(blocksData as Parameters<typeof unpackMultiSheetEnvelope>[0]);
          const multiState: MultiSheetWorkflowState = {
            name: data.workflow_name || inner.name || "",
            sheets: inner.sheets as MultiSheetWorkflowState['sheets'],
            crossSheetConnectors: (inner.crossSheetConnectors ?? []) as MultiSheetWorkflowState['crossSheetConnectors'],
            activeSheetIndex: inner.activeSheetIndex ?? 0,
            coordinateOrigin: data.coordinate_origin === "center" ? "center" : undefined,
          };
          leanState = parseMultiSheetState(multiState) as MultiSheetWorkflowState | null;
        } else {
          // Single-sheet: reassemble WorkflowState from individual columns
          const state: WorkflowState = {
            name: data.workflow_name || "",
            blocks: data.blocks as WorkflowState["blocks"],
            connectors: data.connectors as WorkflowState["connectors"],
            groups: data.groups as WorkflowState["groups"],
            coordinateOrigin: data.coordinate_origin === "center" ? "center" : undefined,
          };
          leanState = parseWorkflowState(state) as WorkflowState | null;
        }
      }

      if (!leanState) return null;

      // Lager C: hämta entity_details och hydrera tillbaka description/
      // icon/subject/to/label/waypoints i blocks/connectors. Tom details-
      // array (legacy data eller ingen edit gjord) → leanState as-is.
      try {
        const details = await fetchAllDetailsForWorkflow(id);
        return mergeFromStorage(leanState, details);
      } catch (err) {
        logger.warn('Lager C: fetchAllDetailsForWorkflow failed — returnerar lean as-is', {
          workflowId: id,
          error: err instanceof Error ? err.message : String(err),
        });
        return leanState;
      }
    },
    [connected, getCurrentUserId, getClient],
  );

  /**
   * Load a workflow shared via co-op. Skips the `created_by` filter — RLS
   * decides access (owner OR active coop member with coop_enabled=true).
   */
  const loadCoopWorkflow = useCallback(
    async (id: string): Promise<WorkflowState | MultiSheetWorkflowState | null> => {
      if (!connected) return null;
      const idResult = documentIdSchema.safeParse(id);
      if (!idResult.success) throw new Error("Invalid document ID format");

      const userId = await getCurrentUserId();
      if (!userId) return null;

      const client = getClient();
      const { data, error } = await client
        .from("workflows")
        .select("blocks, connectors, groups, workflow_name, coordinate_origin, coop_enabled, created_by")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (!data) return null;

      let leanCoopState: WorkflowState | MultiSheetWorkflowState | null;

      if (data.coordinate_origin === 'encrypted') {
        // Encrypted blob — only the owner can decrypt (key derived from
        // their email). Coop members will fail here; that's intentional
        // until co-op shares wrap the workspace key.
        const { data: sess } = await client.auth.getSession();
        const email = sess.session?.user?.email;
        if (!email) throw new Error('Authentication required to decrypt workspace');
        const { decryptWorkspace } = await import('@/lib/workspaceEncryption');
        const decrypted = await decryptWorkspace<WorkflowState | MultiSheetWorkflowState>(
          data.blocks as unknown as string,
          email,
        );
        const named = { ...decrypted, name: data.workflow_name || decrypted.name || '' };
        leanCoopState = isMultiSheetState(named)
          ? (parseMultiSheetState(named) as MultiSheetWorkflowState | null)
          : (parseWorkflowState(named as WorkflowState) as WorkflowState | null);
      } else {
        const blocksData = data.blocks as Record<string, unknown>;
        if (blocksData && typeof blocksData === "object" && "_multiSheet" in blocksData) {
          const inner = unpackMultiSheetEnvelope(blocksData as Parameters<typeof unpackMultiSheetEnvelope>[0]);
          const multiState: MultiSheetWorkflowState = {
            name: data.workflow_name || inner.name || "",
            sheets: inner.sheets as MultiSheetWorkflowState["sheets"],
            crossSheetConnectors: (inner.crossSheetConnectors ?? []) as MultiSheetWorkflowState["crossSheetConnectors"],
            activeSheetIndex: inner.activeSheetIndex ?? 0,
            coordinateOrigin: data.coordinate_origin === "center" ? "center" : undefined,
          };
          leanCoopState = parseMultiSheetState(multiState) as MultiSheetWorkflowState | null;
        } else {
          const state: WorkflowState = {
            name: data.workflow_name || "",
            blocks: data.blocks as WorkflowState["blocks"],
            connectors: data.connectors as WorkflowState["connectors"],
            groups: data.groups as WorkflowState["groups"],
            coordinateOrigin: data.coordinate_origin === "center" ? "center" : undefined,
          };
          leanCoopState = parseWorkflowState(state) as WorkflowState | null;
        }
      }

      if (!leanCoopState) return null;

      // Lager C: hydrera entity_details. RLS:en (can_read_workflow)
      // tillåter coop-member-läs så denna fungerar för delade workflows.
      try {
        const details = await fetchAllDetailsForWorkflow(id);
        return mergeFromStorage(leanCoopState, details);
      } catch {
        return leanCoopState;
      }
    },
    [connected, getCurrentUserId, getClient],
  );

  /**
   * Set the `coop_enabled` flag on a workflow the current user owns. Gates
   * whether collaborators granted via `workspace_collaborators` can see it.
   */
  const setCoopEnabled = useCallback(
    async (id: string, enabled: boolean): Promise<boolean> => {
      if (!connected) return false;
      const idResult = documentIdSchema.safeParse(id);
      if (!idResult.success) throw new Error("Invalid document ID format");
      const userId = await getCurrentUserId();
      if (!userId) return false;
      const client = getClient();
      const { error } = await client
        .from("workflows")
        .update({ coop_enabled: enabled })
        .eq("id", id)
        .eq("created_by", userId);
      if (error) throw error;
      return true;
    },
    [connected, getCurrentUserId, getClient],
  );

  const deleteDocument = useCallback(
    async (id: string): Promise<boolean> => {
      if (!connected) return false;

      const idResult = documentIdSchema.safeParse(id);
      if (!idResult.success) {
        throw new Error("Invalid document ID format");
      }

      const userId = await getCurrentUserId();
      if (!userId) {
        logger.warn("No authenticated user — cannot delete document");
        return false;
      }

      const client = getClient();
      const { error } = await client.from("workflows").delete().eq("id", id).eq("created_by", userId);

      if (error) throw error;

      // Batch D2: GC the 3D-overlay localStorage bucket so a deleted
      // document doesn't leave stale `fw.threeD.overlay.<id>*` blobs
      // sitting on disk. Failures here are swallowed — the row delete
      // already succeeded.
      try {
        clearOverlayScope(id);
      } catch {
        /* best-effort cleanup */
      }

      return true;
    },
    [connected, getCurrentUserId, getClient],
  );


  return {
    isConnected: connected,
    connectionDebugInfo: debugInfo,
    saveDocument,
    listDocuments,
    loadDocument,
    loadCoopWorkflow,
    setCoopEnabled,
    deleteDocument,
  };
}
